import { LoadingButton } from "@mui/lab";
import { Button, Grid, MenuItem, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { FileSection, FormDatePicker, FormTextField } from "components/atoms";
import { CommonModal } from "components/molecules";
import { FormikHelpers, FormikProvider, useFormik } from "formik";
import { TimeHelper } from "helpers";
import { isEmpty, isUndefined } from "lodash";
import { IImportPeriodCycleCountRequest } from "model";
import { ECountType } from "model/Type/ECountType";
import moment, { Moment } from "moment";
import React, { Fragment, useEffect } from "react";
import { CycleCountActions, ProductionPlanActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";

const _fileId = "import-periodic-cycle-count";

interface IImportPeriodicCycleCount {
  countType: string;
  fromDate: Moment;
  toDate: Moment;
  file?: File;
  description?: string;
}
const ImportPeriodicCycleCountType = (props: {
  open: boolean;
  onClose(): void;
}) => {
  const dispatch = useAppDispatch();
  const { open, onClose } = props;
  const fileRef = React.useRef<any>();
  const action = useAppSelector((state) => state.CycleCountReducer.action);

  const handleFileSelected = (files: FileList) => {
    if (files) {
      const file = files.item(0);
      formik.setFieldValue("file", file);
    }
  };
  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  const formik = useFormik<IImportPeriodicCycleCount>({
    initialValues: {
      countType: "",
      fromDate: moment(),
      toDate: moment(),
      description: "",
    },
    onSubmit: (
      values: IImportPeriodicCycleCount,
      { validateForm }: FormikHelpers<IImportPeriodicCycleCount>
    ) => {
      validateForm().then(() => {
        const request: IImportPeriodCycleCountRequest = {
          cycleCountPeriodId: "",
          description: values.description,
          file: values.file,
          fromDate: moment(values.fromDate).format("MM-DD-YYYY"),
          toDate: moment(values.toDate).format("MM-DD-YYYY"),
        };
        if (values.countType === "L") {
          dispatch(
            CycleCountActions.importPeriodicCycleCountByLocation.request(
              request
            )
          );
        } else {
          dispatch(
            CycleCountActions.importPeriodicCycleCountByProduct.request(request)
          );
        }
      });
    },
  });

  useEffect(() => {
    if (
      action.includes(
        CycleCountActions.importPeriodicCycleCountByLocation.successName
      ) ||
      action.includes(
        CycleCountActions.importPeriodicCycleCountByProduct.successName
      ) ||
      action.includes(
        CycleCountActions.importPeriodicCycleCountByLocation.failedName
      ) ||
      action.includes(
        CycleCountActions.importPeriodicCycleCountByProduct.failedName
      )
    ) {
      onClose();
    }
  }, [action]);

  const renderFooter = () => {
    return (
      <Footer
        disableConfirm={
          isUndefined(formik.values.file) || isEmpty(formik.values.countType)
        }
        onClose={onClose}
        onConfirm={formik.submitForm}
      />
    );
  };

  return (
    <Fragment>
      <FileSection
        ref={fileRef}
        id={_fileId}
        onFileSelected={handleFileSelected}
      />
      <FormikProvider value={formik}>
        <CommonModal
          title={"Nhập phiếu kiểm kê định kì"}
          open={open}
          FooterComponent={renderFooter()}
          handleClose={onClose}
        >
          <Grid
            sx={{ width: "100%" }}
            container
            xs={12}
            alignItems="center"
            rowGap={2}
          >
            <Grid container xs={12} item columnSpacing={2}>
              <Grid xs={12} item>
                <FormTextField
                  required
                  label={"Loại kiểm kê"}
                  select
                  id="countType"
                >
                  <MenuItem value={ECountType.LOCATION}>
                    {I18nTranslator(translations.type.location)}
                  </MenuItem>
                  <MenuItem value={ECountType.PRODUCT}>
                    {I18nTranslator(translations.type.product)}
                  </MenuItem>
                </FormTextField>
              </Grid>
            </Grid>
            <Grid container xs={12} item columnSpacing={2}>
              <Grid xs={6} item>
                <FormDatePicker
                  id="fromDate"
                  label={I18nTranslator(translations.fromDate)}
                />
              </Grid>
              <Grid xs={6} item>
                <FormDatePicker
                  id="toDate"
                  label={I18nTranslator(translations.toDate)}
                />
              </Grid>
            </Grid>
            <Grid container xs={12} item columnSpacing={2}>
              <Grid xs={12} item>
                <FormTextField
                  label={I18nTranslator(translations.masterData.description)}
                  placeholder={I18nTranslator(
                    translations.placeholder.description
                  )}
                  id="description"
                />
              </Grid>
            </Grid>

            <Grid container xs={12} item columnSpacing={2}>
              <Grid xs={12} item>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <label htmlFor={_fileId}>
                    <Button component={"span"} variant="contained">
                      {I18nTranslator(translations.button.chooseDocument)}
                    </Button>
                  </label>
                  <Typography
                    sx={{
                      marginLeft: "10px",
                      fontWeight: isUndefined(formik.values.file) ? "0" : "500",
                      fontSize: "18px",
                    }}
                  >
                    {isUndefined(formik.values.file)
                      ? I18nTranslator(translations.receive.notFoundDocument)
                      : formik.values.file?.name}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </CommonModal>
      </FormikProvider>
    </Fragment>
  );
};

const Footer = (props: {
  disableConfirm: boolean;
  onConfirm(): void;
  onClose(): void;
}) => {
  const { disableConfirm, onConfirm, onClose } = props;
  const action = useAppSelector((state) => state.CycleCountReducer.action);
  return (
    <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
      <Button onClick={onClose}>
        {I18nTranslator(translations.button.cancel)}
      </Button>
      <LoadingButton
        loading={
          action.includes(
            CycleCountActions.importPeriodicCycleCountByLocation.requestName
          ) ||
          action.includes(
            CycleCountActions.importPeriodicCycleCountByProduct.requestName
          )
        }
        disabled={disableConfirm}
        onClick={onConfirm}
        variant="contained"
      >
        {I18nTranslator(translations.button.importDocs)}
      </LoadingButton>
    </Stack>
  );
};
export default ImportPeriodicCycleCountType;
